"use client";

import Image from "next/image";
import { Clock, BadgeCheck } from "lucide-react";
import { getBusinessTimezoneDisplay } from "@/lib/timezone";

export interface BookingConfirmationProps {
  practitionerName?: string;
  practitionerRole?: string;
  practitionerImage?: string;
  supportOption?: string;
  duration?: string;
  price?: number;
  appointmentDate?: Date | null;
  appointmentTime?: string | null;
}

const BookingConfirmation = ({
  practitionerName = "Alexander Rodriguez",
  practitionerRole = "Founder, Counsellor, Life Coach and Allied Health",
  practitionerImage,
  supportOption = "Family Session",
  duration = "60 min",
  price = 130,
  appointmentDate,
  appointmentTime,
}: BookingConfirmationProps) => {
  const formatDate = (date: Date) => {
    return date.toLocaleDateString("en-US", {
      weekday: "long",
      month: "short",
      day: "numeric",
      year: "numeric",
    });
  };

  const formatTime = (time: string) => {
    const existingAmpm = time.match(/\s*(AM|PM)$/i);
    if (existingAmpm) {
      const cleaned = time.replace(/\s*(AM|PM)$/i, "").trim();
      const [hours, minutes] = cleaned.split(":");
      const hour = parseInt(hours);
      const displayHour = hour > 12 ? hour - 12 : hour === 0 ? 12 : hour;
      return `${displayHour}:${minutes} ${existingAmpm[1].toUpperCase()}`;
    }
    const [hours, minutes] = time.split(":");
    const hour = parseInt(hours);
    const ampm = hour >= 12 ? "PM" : "AM";
    const displayHour = hour > 12 ? hour - 12 : hour === 0 ? 12 : hour;
    return `${displayHour}:${minutes} ${ampm}`;
  };

  const stripeFee = 5.81;
  const total = price + stripeFee;

  return (
    <div className="flex w-full items-center justify-center px-4 md:px-0">
      {/* Confirmation Card */}
      <div className="relative w-full max-w-[950px] overflow-hidden rounded-[10px] border border-v3-primary bg-white">
        {/* Background Image - Positioned to show on the left, hidden on mobile */}
        <div className="absolute inset-y-0 left-0 hidden w-[calc(100%-478px+150px)] lg:block">
          <Image
            src="/images/v3/booking/payment/942ab4773d870c2042c43cb90d2f47f23eb3e6eb.jpg"
            alt=""
            fill
            className="object-cover"
            sizes="(max-width: 1024px) 0px, 544px"
          />
          {/* Gradient fade from image to white on the right */}
          <div
            className="absolute inset-y-0 right-0 w-[600px]"
            style={{
              background:
                "linear-gradient(to right, transparent 0%, rgba(255,255,255,0.3) 15%, rgba(255,255,255,0.6) 35%, rgba(255,255,255,0.85) 55%, rgba(255,255,255,1) 75%)",
            }}
          />
        </div>

        {/* Content */}
        <div className="relative flex justify-center lg:justify-end">
          <div className="w-full rounded-[10px] p-4 md:p-[30px] lg:w-[478px] lg:shrink-0">
            <div className="flex flex-col">
              {/* Practitioner Info */}
              <div className="p-2.5">
                <div className="flex items-center gap-2.5">
                  <div className="relative size-[50px] overflow-hidden rounded-full md:size-[60px]">
                    <Image
                      src={practitionerImage || "/images/v3/booking/practionerSelection/practitioner-placeholder.jpg"}
                      alt={practitionerName}
                      fill
                      className="object-cover"
                      sizes="(max-width: 768px) 50px, 60px"
                    />
                  </div>
                  <div className="flex flex-col gap-1.5 p-2.5 md:gap-2.5">
                    <span className="font-onest text-lg font-semibold text-black md:text-xl">
                      {practitionerName}
                    </span>
                    <span className="font-onest text-sm font-normal text-black md:text-base">
                      {practitionerRole}
                    </span>
                  </div>
                </div>
              </div>

              {/* Support Option */}
              <div className="p-2.5">
                <div className="border-l-2 border-v3-primary pl-2.5">
                  <div className="p-2.5">
                    <span className="font-onest text-sm font-semibold text-black md:text-base">
                      {supportOption}
                    </span>
                  </div>
                  <div className="flex items-center gap-4 p-2.5 md:gap-7">
                    <div className="flex items-center gap-1.5">
                      <Clock className="size-5 text-v3-primary md:size-6" />
                      <span className="font-onest text-lg font-medium text-v3-primary md:text-xl">
                        {duration}
                      </span>
                    </div>
                    <span className="font-onest text-lg font-bold text-black/60 md:text-xl">
                      ${price}
                    </span>
                  </div>
                </div>
              </div>

              {/* Appointment Details */}
              <div className="rounded-[10px] bg-v3-primary/10 p-2.5">
                <div className="p-2.5">
                  <span className="font-onest text-sm font-semibold text-black md:text-base">
                    Appointment
                  </span>
                </div>
                <div className="flex flex-wrap gap-1.5 px-2.5 md:gap-2.5">
                  <span className="font-onest text-sm font-normal text-black/60 md:text-base">
                    {appointmentDate
                      ? formatDate(appointmentDate)
                      : "Tuesday, Jun 16, 2026"}
                  </span>
                  <span className="font-onest text-sm font-normal text-black/60 md:text-base">
                    {appointmentTime ? formatTime(appointmentTime) : "2:00 PM"}
                  </span>
                </div>
                <div className="px-2.5 pb-2.5">
                  <span className="font-onest text-[10px] font-normal text-black/30 md:text-xs">
                    {getBusinessTimezoneDisplay(appointmentDate)}
                  </span>
                </div>
              </div>

              {/* Total Cost */}
              <div className="border-b border-black/20 p-2.5">
                <div className="p-2.5">
                  <span className="font-onest text-sm font-semibold text-black md:text-base">
                    Total Cost
                  </span>
                </div>
                <div className="flex items-center justify-between p-2.5">
                  <span className="font-onest text-base font-medium text-black/60 md:text-xl">
                    Session Fee
                  </span>
                  <span className="font-onest text-base font-normal text-black/60 md:text-xl">
                    ${price} AUD
                  </span>
                </div>
                <div className="flex items-center justify-between p-2.5">
                  <span className="font-onest text-base font-medium text-black/60 md:text-xl">
                    Stripe Fee
                  </span>
                  <span className="font-onest text-base font-normal text-black/60 md:text-xl">
                    ${stripeFee.toFixed(2)} AUD
                  </span>
                </div>
              </div>

              {/* Total */}
              <div className="p-2.5">
                <div className="flex items-center justify-between p-2.5">
                  <span className="font-onest text-base font-medium text-black/60 md:text-xl">
                    Total
                  </span>
                  <span className="font-onest text-base font-normal text-black/60 md:text-xl">
                    ${total.toFixed(2)} AUD
                  </span>
                </div>
              </div>

              {/* Payment Confirmed Badge */}
              <div className="flex items-center p-2.5">
                <div
                  className="flex items-center rounded-[15px] border-2 border-white bg-v3-primary p-2 md:rounded-[20px] md:border-4 md:p-2.5"
                  style={{
                    boxShadow: "inset 0px 0px 20px 0px rgba(255,255,255,0.5)",
                  }}
                >
                  <BadgeCheck className="size-5 text-white md:size-6" />
                </div>
                <div className="p-2.5">
                  <span className="font-onest text-xl font-semibold text-black md:text-2xl">
                    Payment Confirmed
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BookingConfirmation;
